import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { mergeContact } from 'api/contacts/contacts';
import axios, { AxiosError } from 'axios';
import * as Form from 'components/form';
import * as Select from 'components/select';
import { UseFormSearchableSelect } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';

interface MergeContactDialogProps extends AlertDialog.AlertDialogRootProps {}

interface MergeContactFormValues {
    selectedContact?: { id: string; label: string; value: Contact }[];
}

const ERROR_MESSAGE = 'Please select a contact';

const MergeContactDialog = (props: MergeContactDialogProps) => {
    const toast = useToast();
    const { children, ...rest } = props;
    const queryClient = useQueryClient();
    const history = useHistory();
    const { data: currentContact } = useCurrentContact();
    const { accountId } = useCurrentAccount();
    const methods = Form.useForm<MergeContactFormValues>({
        defaultValues: {
            selectedContact: undefined,
        },
        shouldFocusError: false,
    });
    const { watch, path, control, handleSubmit } = methods;
    const watchedSelectedContact = watch('selectedContact');
    const selectedContact = watchedSelectedContact?.[0].value;
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { data, ref } = useInfiniteContacts({
        query: debouncedSearch,
        type: ContactType.Individual,
    });
    const contacts = data?.pages.filter(contact => contact.id !== currentContact.id) ?? [];

    const { mutate: submit } = useMutation({
        mutationFn: (values: MergeContactFormValues) => {
            if (!values?.selectedContact?.[0]) {
                throw new Error(ERROR_MESSAGE);
            }

            return mergeContact(
                accountId,
                currentContact.id,
                parseInt(values.selectedContact[0].id),
            );
        },
        onSuccess: data => {
            history.replace(`/accounts/${accountId}/contacts/${data.id}`);
            toast.success('Successfully merged contact!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
                type: 'all',
                predicate: query => !query.queryKey.includes(currentContact.id),
                refetchType: 'none',
            });
        },
        onError: (e: Error | AxiosError) => {
            let nextErrorMessage =
                (axios.isAxiosError(e) && e.response?.data?.message) ??
                e.message ??
                'Failed to merge contact';

            toast.error(nextErrorMessage);
        },
    });

    return (
        <Form.Provider {...methods}>
            <AlertDialog.Root type={'info'} {...rest}>
                {children}
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content>
                        <form
                            className={'contents'}
                            onSubmit={handleSubmit(submit as SubmitHandler<MergeContactFormValues>)}
                        >
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Merge contact</AlertDialog.Title>
                                <Form.Container>
                                    <Form.Label name={path('selectedContact')}>
                                        Search for a contact to merge
                                    </Form.Label>
                                    <UseFormSearchableSelect
                                        control={control}
                                        name={'selectedContact'}
                                        rules={{ required: ERROR_MESSAGE }}
                                        showTags={true}
                                        localSearch={false}
                                        multi={false}
                                        inputProps={{
                                            onChange: e => setSearch(e.target.value),
                                        }}
                                    >
                                        {contacts.map(contact => {
                                            const fullName = contact.name_display;

                                            return (
                                                <Select.ListItem
                                                    key={contact.id}
                                                    value={{
                                                        id: contact.id.toString(),
                                                        label: fullName,
                                                        value: contact,
                                                    }}
                                                >
                                                    {fullName}{' '}
                                                    {contact.primary_email &&
                                                        `(${contact.primary_email})`}
                                                </Select.ListItem>
                                            );
                                        })}
                                        <div ref={ref} />
                                    </UseFormSearchableSelect>
                                    <Form.Error name={path('selectedContact')} />
                                </Form.Container>
                                {selectedContact && (
                                    <AlertDialog.Description>
                                        You are about to merge{' '}
                                        <span className={'font-medium'}>
                                            {currentContact.name_display}
                                        </span>{' '}
                                        into{' '}
                                        <span className={'font-medium'}>
                                            {selectedContact.name_display}
                                        </span>
                                        . You won't be able to undo this action.
                                    </AlertDialog.Description>
                                )}
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton className="w-1/2">
                                    Merge contact
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </Form.Provider>
    );
};

const MergeContactDialogTrigger = AlertDialog.Trigger;

const Root = MergeContactDialog;
const Trigger = MergeContactDialogTrigger;

export {
    MergeContactDialog,
    MergeContactDialogTrigger,
    //
    Root,
    Trigger,
};
